import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { ClientPage } from './pages/ClientPage';
import { AgentPage } from './pages/AgentPage';
import { AgentHomePage } from './pages/AgentHomePage';
import { SupervisorPage } from './pages/SupervisorPage';
import { LoginPage } from './pages/LoginPage';
import { AdminPage } from './pages/AdminPage';

const SERVER_URL = window.location.origin;

export default function App() {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [agent, setAgent] = useState(() => {
    const stored = localStorage.getItem('auth_agent');
    return stored ? JSON.parse(stored) : null;
  });
  const [checkingAuth, setCheckingAuth] = useState(!!token);
  const [activeSessionCode, setActiveSessionCode] = useState(null);

  const path = window.location.pathname;
  const isAgent = path === '/agent' || path === '/agent/';
  const isAdmin = path === '/admin' || path === '/admin/';
  const isClient = path === '/klijent' || path === '/klijent/';

  useEffect(() => {
    if (!token || !(isAgent || isAdmin)) {
      setCheckingAuth(false);
      return;
    }
    fetch(`${SERVER_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('invalid');
        return res.json();
      })
      .then(data => {
        setAgent(data.agent);
        localStorage.setItem('auth_agent', JSON.stringify(data.agent));
      })
      .catch(() => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_agent');
        setToken(null);
        setAgent(null);
      })
      .finally(() => setCheckingAuth(false));
  }, []);

  useEffect(() => {
    if (checkingAuth) return;
    if ((isAgent || isAdmin) && !token) return;
    if (!(isAgent || isAdmin || isClient)) return;

    const s = io(SERVER_URL, { transports: ['websocket'] });
    s.on('connect', () => { setConnected(true); setSocket(s); });
    s.on('disconnect', () => setConnected(false));
    return () => s.disconnect();
  }, [checkingAuth, token, isAgent, isAdmin, isClient]);

  const handleLogin = (newToken, newAgent) => {
    setToken(newToken);
    setAgent(newAgent);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_agent');
    setToken(null);
    setAgent(null);
    if (socket) socket.disconnect();
    setSocket(null);
    setConnected(false);
    setActiveSessionCode(null);
  };

  if (!isAgent && !isAdmin && !isClient) {
    const bigBtn = (primary) => ({
      display: 'block', padding: '18px 20px', borderRadius: 14, textDecoration: 'none', textAlign: 'left',
      background: primary ? '#185FA5' : '#fff', color: primary ? '#fff' : '#1A2430',
      border: primary ? 'none' : '1px solid #E3E8EE',
      boxShadow: primary ? '0 4px 14px rgba(24,95,165,0.28)' : '0 1px 2px rgba(20,30,45,0.05)',
      marginBottom: 12
    });
    const btnTitle = { fontSize: 18, fontWeight: 700, marginBottom: 4 };
    const btnSub = (primary) => ({ fontSize: 13.5, lineHeight: 1.45, color: primary ? 'rgba(255,255,255,0.85)' : '#5C6670' });

    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA', fontFamily: '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', position: 'relative' }}>
        {/* Ulaz za agente podrske - diskretna ikonica, klijentima nije potrebna */}
        <a href="/agent" title="Prijava za agente podrške" aria-label="Prijava za agente podrške"
          style={{ position: 'absolute', top: 14, left: 14, width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9AA4AE', textDecoration: 'none', background: 'transparent' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
          </svg>
        </a>

        <div style={{ maxWidth: 440, margin: '0 auto', padding: '72px 20px 40px' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#1A2430', marginBottom: 8 }}>Screen Support</div>
            <div style={{ fontSize: 15, color: '#5C6670', lineHeight: 1.55 }}>
              Agent podrške će videti vaš ekran i pokazati vam šta da uradite.
            </div>
          </div>

          <a href="/preuzmi" style={bigBtn(true)}>
            <div style={btnTitle}>⬇ Preuzmi aplikaciju</div>
            <div style={btnSub(true)}>Za Android telefon ili Windows računar. Preporučeno.</div>
          </a>

          <a href="/klijent" style={bigBtn(false)}>
            <div style={btnTitle}>🖥 Podeli ekran iz browsera</div>
            <div style={btnSub(false)}>Bez instalacije — za Mac, Linux ili ako ne možete da instalirate aplikaciju.</div>
          </a>
        </div>
      </div>
    );
  }

  if (checkingAuth) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', background: '#f5f5f5', flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 32, height: 32, border: '3px solid #185FA5', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 14, color: '#666' }}>Provera prijave...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if ((isAgent || isAdmin) && !token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (isAdmin && agent?.role !== 'admin') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', background: '#f5f5f5' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '40px 32px', textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#791F1F' }}>Nemate pristup</div>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>Admin panel je dostupan samo administratorima.</div>
          <a href="/agent" style={{ display: 'inline-block', padding: '10px 20px', background: '#185FA5', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>Nazad na agent</a>
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', background: '#f5f5f5', flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 32, height: 32, border: '3px solid #185FA5', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 14, color: '#666' }}>Povezivanje sa serverom...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (isAgent) {
    if (agent?.role === 'supervisor') {
      return (
        <SupervisorPage
          socket={socket}
          token={token}
          agent={agent}
          onLogout={handleLogout}
        />
      );
    }
    if (!activeSessionCode) {
      return (
        <AgentHomePage
          socket={socket}
          token={token}
          agent={agent}
          onLogout={handleLogout}
          onAcceptSession={(code) => setActiveSessionCode(code)}
        />
      );
    }
    return (
      <AgentPage
        socket={socket}
        token={token}
        agent={agent}
        onLogout={handleLogout}
        sessionCode={activeSessionCode}
        onEndSession={() => setActiveSessionCode(null)}
      />
    );
  }
  if (isAdmin) return <AdminPage token={token} agent={agent} onLogout={handleLogout} />;
  if (isClient) return <ClientPage socket={socket} />;

  return null;
}
